import { db } from '../config/firebase';
import { addDoc, collection, doc, getDoc, getDocs, limit, orderBy, query, setDoc, where } from 'firebase/firestore';
import { CUKCUK_IMPORTS_COLLECTION } from '../constants/cukcuk';
import type { CukcukImportHistory } from '../types/cukcukImportHistory';

const COLLECTION_NAME = CUKCUK_IMPORTS_COLLECTION;
const omitUndefined = <T extends Record<string, unknown>>(value: T): T =>
  Object.fromEntries(Object.entries(value).filter(([, field]) => field !== undefined)) as T;
const historyId = (type: CukcukImportHistory['type'], fileHash: string): string => `${type}_${fileHash}`;
const fromDocument = (document: { id: string; data(): any }): CukcukImportHistory => {
  const data = document.data();
  return {
    id: document.id, type: data.type, fileName: data.fileName, fileHash: data.fileHash,
    fileSize: data.fileSize, importedAt: data.importedAt?.toDate?.() ?? new Date(data.importedAt),
    counts: data.counts, createdAt: data.createdAt?.toDate?.() ?? undefined,
  };
};

export class CukcukImportHistoryService {
  async addRecord(record: Omit<CukcukImportHistory, 'id' | 'createdAt'>): Promise<CukcukImportHistory> {
    try {
      const importedAt = record.importedAt ?? new Date();
      const createdAt = new Date();
      const payload = omitUndefined({ ...record, importedAt, createdAt });
      const document = record.fileHash
        ? doc(db, COLLECTION_NAME, historyId(record.type, record.fileHash))
        : await addDoc(collection(db, COLLECTION_NAME), payload);
      if (record.fileHash) await setDoc(document, payload);
      return { id: document.id, ...record, importedAt, createdAt };
    } catch (error) {
      throw new Error('Cannot save CUKCUK import history: ' + error);
    }
  }

  async getRecent(maxRecords = 20): Promise<CukcukImportHistory[]> {
    try {
      const snapshot = await getDocs(query(collection(db, COLLECTION_NAME), orderBy('importedAt', 'desc'), limit(maxRecords)));
      return snapshot.docs.map(fromDocument);
    } catch (error) {
      throw new Error('Cannot retrieve CUKCUK import history: ' + error);
    }
  }

  async findByFileHash(fileHash: string, type: CukcukImportHistory['type']): Promise<CukcukImportHistory | null> {
    try {
      const snapshot = await getDoc(doc(db, COLLECTION_NAME, historyId(type, fileHash)));
      if (snapshot.exists()) return fromDocument(snapshot);
      const legacy = await getDocs(query(collection(db, COLLECTION_NAME), where('fileHash', '==', fileHash), limit(10)));
      const records = legacy.docs.map(fromDocument).filter((record) => record.type === type)
        .sort((a, b) => b.importedAt.getTime() - a.importedAt.getTime());
      return records[0] ?? null;
    } catch (error) {
      throw new Error('Cannot retrieve CUKCUK import history: ' + error);
    }
  }
}
